import React from 'react'
import { plural, Trans } from '@lingui/macro'

import Price from 'components/price'
import InterpolateElement from 'components/utils/InterpolateElement'
import { i18n } from 'utils/i18n'

interface Props {
  hasDiscount: boolean
  discountInformation?: null | EF.DiscountInformation
  trialPeriodDays: number
  currencyCode: EF.supportedCurrency
  fullPrice: number
  displayPrice: number
  renewalDate: string
  renewalPeriodText: string
  renewalPeriodMonths: number
  className?: string
}

const DiscountDescription = ({
  discountInformation,
  displayPrice,
  currencyCode,
  renewalDate,
  renewalPeriodMonths,
  fullPrice,
}: Props) => {
  if (discountInformation?.duration === 'forever') {
    const discountPercentage = discountInformation?.percent_off || 0
    return (
      <Trans id="signup.payment.plan.description.discount.forever">
        You will have a lifetime discount of{' '}
        <strong>{discountPercentage}%</strong> at the price of{' '}
        <strong>
          <Price value={displayPrice} currencyCode={currencyCode} />
        </strong>
        . Your subscription will renew on <strong>{renewalDate}</strong>.
      </Trans>
    )
  }

  const descriptionCopy = i18n._(
    plural({
      value: renewalPeriodMonths,
      one: `signup.payment.plan.description.discount.one`,
      other: `signup.payment.plan.description.discount.other`,
    })
  )

  return (
    <InterpolateElement
      text={descriptionCopy}
      parameters={{
        price: (
          <strong>
            <Price value={fullPrice} currencyCode={currencyCode} />
          </strong>
        ),
        renewalDate: <strong>{renewalDate}</strong>,
      }}
    />
  )
}

const TrialDescription = ({ trialPeriodDays }: Props) => {
  return (
    <Trans id="signup.payment.plan.description.trial">
      You will be charged by the end of the{' '}
      <strong>{trialPeriodDays} day trial period</strong>. You can cancel your
      subscription anytime at least 24 hours before the end of the subscription
      period. Your subscription will be renewed every year. You can cancel at
      any time.
    </Trans>
  )
}

const DefaultDescription = ({ renewalPeriodText, renewalDate }: Props) => {
  return (
    <Trans id="signup.payment.plan.description">
      Your subscription will be renewed automatically after {renewalPeriodText}{' '}
      on <strong>{renewalDate}</strong>. You can cancel at any time at least 24
      hours before the end of the subscription period.
    </Trans>
  )
}

const PlanDescription = (props: Props) => {
  return (
    <p className={props.className}>
      {props.hasDiscount ? (
        <DiscountDescription {...props} />
      ) : props.trialPeriodDays ? (
        <TrialDescription {...props} />
      ) : (
        <DefaultDescription {...props} />
      )}
    </p>
  )
}

export default PlanDescription
